import { type PaymentNotice, type PaymentNoticeState, paymentNotice } from '@zerohuman/shared'
import { type ComputedRef, computed, type Ref, ref } from 'vue'
import { api, type Me } from '@/api'
import { grantAllowsScreen } from '@/lib/navGrant'

/** The banner the shell shows while the plan's last payment has not gone through. */
export type PaymentBanner = {
    notice: PaymentNotice
    /** Whether the person's access includes Billing, where the button goes. */
    showsButton: boolean
}

/**
 * The past-due banner across the portal: whether the signed-in enterprise's plan is behind on a payment, read when
 * the shell mounts and again after each route change, as the plan-limit banner is. It stays until the payment is
 * collected or the plan ends, and cannot be put away: a plan about to end is not something to dismiss. The words are
 * the shared sentence; the button shows only when the person's access includes Billing, and without it the words say
 * who to tell.
 */
export function usePaymentBanner(me: Ref<Me | null>): {
    banner: ComputedRef<PaymentBanner | null>
    refresh: () => Promise<void>
} {
    const state = ref<PaymentNoticeState | null>(null)

    const banner = computed<PaymentBanner | null>(() => {
        const person = me.value
        if (!person || !state.value) {
            return null
        }
        const showsButton = grantAllowsScreen({ isOwner: person.isOwner, scopes: person.scopes }, '/billing')
        const notice = paymentNotice(state.value, showsButton)
        return notice ? { notice, showsButton } : null
    })

    async function refresh(): Promise<void> {
        if (!me.value) {
            return
        }
        try {
            state.value = await api.paymentNotice()
        } catch {
            // A read that fails keeps what the last one said: the banner never guesses a payment went through.
        }
    }

    return { banner, refresh }
}
